import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import {
  ReportDraft,
  ReportPeriodType,
  ReportDraftStatus,
  ReportRequestItem,
  ReportDeliverableItem,
  REPORT_TEAMS,
  getReportsConfig,
  generateReportBatch,
  listReportDrafts,
  patchReportDraft,
  approveReportDraft,
  dismissReportDraft,
  approveAllReportDrafts,
} from '../services/api';

// ============================================================================
// Reports page — automated team check-in reports.
//
// Each AI agent drafts its human's weekly/monthly check-in from that person's task
// data. NOTHING reaches the official check-ins until a human approves a draft here;
// on approval the cockpit submits it through the check-ins app's API.
//
// This page is the landing target for the check-ins reminder's deep link, e.g.
//   /reports?period_type=weekly&year=2026&period=23
// When opened with a period and no drafts yet, it auto-generates the batch so the
// reviewer just sees drafts to approve.
//
// The per-person reminder email ("Let my AI draft it") adds &person=<slug>. That opens
// the same page scoped to one person: only their draft is generated and shown, so the
// person clicking from their own overdue reminder sees their check-in and nothing else.
// "Show the whole team" clears the scope.
// ============================================================================

const PERSON_LABEL: Record<string, string> = {
  joshua: 'Joshua · Founder',
  subham: 'Subham · Product',
  aazmeer: 'Aazmeer · Growth',
  thadeus: 'Thadeus · Impact',
  zee: 'Zee · Operations / Content',
  payal: 'Payal · AI Platform',
  travis: 'Travis · People & Ops',
  kaja: 'Kaja · DevOps',
  hassan: 'Hassan · Engineering',
  honelign: 'Honelign · Engineering',
  abrham: 'Abrham · AI Platform',
  armin: 'Armin · Science',
  jubaer: 'Jubaer · AI Platform',
  prachi: 'Prachi · AI Platform',
  ojassvi: 'Ojassvi · Product',
  mary: 'Mary · Growth',
  adeola: 'Adeola · Impact',
  joey: 'Joey · Quest Product & Dev Ops',
};
const personLabel = (p: string) => PERSON_LABEL[p] || p.charAt(0).toUpperCase() + p.slice(1);

// ISO week number (matches Python's isocalendar week used by the backend).
const isoWeek = (d: Date): number => {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = (date.getUTCDay() + 6) % 7; // Mon=0..Sun=6
  date.setUTCDate(date.getUTCDate() - dayNum + 3); // nearest Thursday
  const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  const firstDayNum = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayNum + 3);
  return 1 + Math.round((date.getTime() - firstThursday.getTime()) / (7 * 24 * 3600 * 1000));
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const Reports: React.FC = () => {
  const { theme } = useTheme();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const now = new Date();
  const qpType = (params.get('period_type') as ReportPeriodType) || 'weekly';
  const qpYear = params.get('year');
  const qpPeriod = params.get('period');
  const qpPerson = (params.get('person') || '').trim().toLowerCase() || null;

  const [periodType, setPeriodType] = useState<ReportPeriodType>(
    qpType === 'monthly' ? 'monthly' : 'weekly'
  );
  const [year, setYear] = useState<number>(qpYear ? parseInt(qpYear, 10) : now.getFullYear());
  const [period, setPeriod] = useState<number>(
    qpPeriod ? parseInt(qpPeriod, 10) : qpType === 'monthly' ? now.getMonth() + 1 : isoWeek(now)
  );

  // Set when arriving from a person's own reminder: scope the page to just them.
  const [person, setPerson] = useState<string | null>(qpPerson);

  const [drafts, setDrafts] = useState<ReportDraft[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [config, setConfig] = useState<{ configured: boolean; reason: string | null } | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  // Whoever the last batch call skipped because they have never used the Command Center.
  // Only known right after a generate() call; reset whenever the scope changes.
  const [neverUsedCockpit, setNeverUsedCockpit] = useState<string[]>([]);
  const [draftingAnyway, setDraftingAnyway] = useState<string | null>(null);
  const autoRan = useRef(false);

  const periodLabel =
    periodType === 'weekly' ? `Week ${period}, ${year}` : `${MONTHS[period - 1]} ${year}`;

  const load = useCallback(async () => {
    setLoading(true);
    setNeverUsedCockpit([]);
    try {
      const res = await listReportDrafts({
        period_type: periodType,
        year,
        period,
        person: person || undefined,
      });
      // Hide dismissed drafts from the main view.
      setDrafts(res.drafts.filter((d) => d.status !== 'dismissed'));
    } finally {
      setLoading(false);
    }
  }, [periodType, year, period, person]);

  useEffect(() => {
    getReportsConfig().then(setConfig).catch(() => setConfig(null));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const generate = useCallback(async () => {
    setGenerating(true);
    setBanner(null);
    try {
      const res = await generateReportBatch({
        period_type: periodType,
        year,
        period,
        people: person ? [person] : undefined,
      });
      setDrafts(res.drafts.filter((d) => d.status !== 'dismissed'));
      // Fall back to the old field name in case an older backend build is still serving.
      setNeverUsedCockpit(res.skipped_never_used_cockpit || res.skipped_never_logged_in || []);
    } catch (e: any) {
      setBanner(`Could not start generation: ${e?.message || e}`);
    } finally {
      setGenerating(false);
    }
  }, [periodType, year, period, person]);

  // Human override for one person who has never used the Command Center: draft their
  // check-in anyway.
  const draftAnyway = async (p: string) => {
    setDraftingAnyway(p);
    setBanner(null);
    try {
      const res = await generateReportBatch({
        period_type: periodType,
        year,
        period,
        people: [p],
        include_never_used_cockpit: true,
      });
      setDrafts((prev) => {
        const others = prev.filter((d) => d.person !== p);
        return [...others, ...res.drafts].filter((d) => d.status !== 'dismissed');
      });
      setNeverUsedCockpit((prev) => prev.filter((slug) => slug !== p));
    } catch (e: any) {
      setBanner(`Could not draft for ${personLabel(p)}: ${e?.message || e}`);
    } finally {
      setDraftingAnyway(null);
    }
  };

  // Auto-generate once when arriving via a deep link with an explicit period and no drafts.
  useEffect(() => {
    if (autoRan.current) return;
    if (!qpYear || !qpPeriod) return;
    if (loading) return;
    if (drafts.length === 0) {
      autoRan.current = true;
      generate();
    } else {
      autoRan.current = true;
    }
  }, [qpYear, qpPeriod, loading, drafts.length, generate]);

  // Poll while anything is still generating.
  useEffect(() => {
    if (!drafts.some((d) => d.status === 'generating')) return;
    const t = setInterval(load, 1500);
    return () => clearInterval(t);
  }, [drafts, load]);

  const pendingCount = drafts.filter((d) => d.status === 'pending_review').length;

  const onApproveAll = async () => {
    setBanner(null);
    const res = await approveAllReportDrafts({ period_type: periodType, year, period });
    if (!res.ok) {
      setBanner(res.reason || 'Submission is not configured yet.');
    } else {
      setBanner(`Submitted ${res.submitted} of ${res.total} report(s) to the check-ins.`);
    }
    load();
  };

  // ---- styles --------------------------------------------------------------
  const cardStyle: React.CSSProperties = {
    backgroundColor: theme.surface,
    border: `1px solid ${theme.border}`,
    borderRadius: '10px',
    padding: '16px',
    marginBottom: '14px',
  };
  const btn = (bg: string): React.CSSProperties => ({
    backgroundColor: bg,
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '7px',
    padding: '8px 14px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  });
  const ghostBtn: React.CSSProperties = {
    backgroundColor: 'transparent',
    color: theme.textSecondary,
    border: `1px solid ${theme.border}`,
    borderRadius: '7px',
    padding: '8px 14px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  };
  const inputStyle: React.CSSProperties = {
    backgroundColor: theme.background,
    color: theme.text,
    border: `1px solid ${theme.border}`,
    borderRadius: '6px',
    padding: '6px 8px',
    fontSize: '13px',
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px 16px' }}>
      <h1 style={{ color: theme.text, margin: 0 }}>
        {person ? `${personLabel(person)}: check-in draft` : 'Team Reports'}
      </h1>
      <p style={{ color: theme.textSecondary, margin: '6px 0 0', fontSize: '14px', maxWidth: '720px' }}>
        {person ? (
          <>
            Your AI drafted this {periodType} check-in from your task activity in {periodLabel}. Read
            it, fix anything that is off, then approve. It posts to the check-ins for you. Nothing
            reaches the official check-ins until you approve it.{' '}
            <button
              onClick={() => setPerson(null)}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                color: theme.primary,
                fontSize: '14px',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Show the whole team
            </button>
          </>
        ) : (
          <>
            Each agent drafts its person's check-in from their task activity. Review and edit below,
            then approve. Nothing reaches the official check-ins until you approve it.
          </>
        )}
      </p>

      {config && !config.configured && (
        <div
          style={{
            ...cardStyle,
            borderColor: theme.warning,
            marginTop: '16px',
            color: theme.text,
            fontSize: '13px',
          }}
        >
          <strong style={{ color: theme.warning }}>Submission not wired up yet.</strong>{' '}
          {config.reason} You can still generate, edit, and review drafts here; approving will be
          enabled once the check-ins service account is configured (Phase B).
        </div>
      )}

      {/* Controls */}
      <div
        style={{
          ...cardStyle,
          marginTop: '16px',
          display: 'flex',
          gap: '10px',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <select
          value={periodType}
          onChange={(e) => setPeriodType(e.target.value as ReportPeriodType)}
          style={inputStyle}
        >
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly (month-end)</option>
        </select>
        <label style={{ color: theme.textSecondary, fontSize: '13px' }}>
          {periodType === 'weekly' ? 'Week' : 'Month'}{' '}
          <input
            type="number"
            value={period}
            min={1}
            max={periodType === 'weekly' ? 53 : 12}
            onChange={(e) => setPeriod(parseInt(e.target.value, 10) || 1)}
            style={{ ...inputStyle, width: '70px' }}
          />
        </label>
        <label style={{ color: theme.textSecondary, fontSize: '13px' }}>
          Year{' '}
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10) || now.getFullYear())}
            style={{ ...inputStyle, width: '90px' }}
          />
        </label>
        <span style={{ color: theme.textSecondary, fontSize: '13px', marginLeft: 'auto' }}>
          {periodLabel}
        </span>
        <button onClick={generate} disabled={generating} style={btn(theme.primary)}>
          {generating
            ? 'Starting…'
            : person
            ? drafts.length
              ? 'Regenerate'
              : 'Generate my draft'
            : drafts.length
            ? 'Regenerate all'
            : 'Generate drafts'}
        </button>
        {pendingCount > 0 && !person && (
          <button
            onClick={onApproveAll}
            disabled={!config?.configured}
            title={config?.configured ? '' : 'Submission not configured yet'}
            style={{
              ...btn(theme.success),
              opacity: config?.configured ? 1 : 0.5,
              cursor: config?.configured ? 'pointer' : 'not-allowed',
            }}
          >
            Approve all ({pendingCount})
          </button>
        )}
      </div>

      {banner && (
        <div style={{ ...cardStyle, color: theme.text, fontSize: '13px' }}>{banner}</div>
      )}

      {neverUsedCockpit.length > 0 && (
        <div style={{ ...cardStyle, color: theme.text, fontSize: '13px' }}>
          <div style={{ fontWeight: 600, marginBottom: '4px' }}>
            No draft was made for {neverUsedCockpit.length}{' '}
            {neverUsedCockpit.length === 1 ? 'person' : 'people'} who{' '}
            {neverUsedCockpit.length === 1 ? 'has' : 'have'} not used the Command Center yet.
          </div>
          <div style={{ color: theme.textSecondary, marginBottom: '10px' }}>
            They were sent the normal reminder instead, with no AI drafting button. You can still
            draft one by hand below.
          </div>
          {neverUsedCockpit.map((p) => (
            <div
              key={p}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '6px',
              }}
            >
              <span style={{ color: theme.text, flex: 1 }}>{personLabel(p)}</span>
              <button
                onClick={() => draftAnyway(p)}
                disabled={draftingAnyway === p}
                style={ghostBtn}
              >
                {draftingAnyway === p ? 'Drafting…' : 'Draft anyway'}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Drafts */}
      {loading && drafts.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', color: theme.textSecondary }}>Loading…</div>
      ) : drafts.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', color: theme.textSecondary }}>
          {person
            ? `No draft for ${periodLabel} yet. Click “Generate my draft” and your AI will write it from your task activity.`
            : `No drafts for ${periodLabel} yet. Click “Generate drafts” to have each agent draft its person's check-in.`}
        </div>
      ) : (
        drafts.map((d) => (
          <DraftCard
            key={d.id}
            draft={d}
            theme={theme}
            cardStyle={cardStyle}
            btn={btn}
            ghostBtn={ghostBtn}
            inputStyle={inputStyle}
            canSubmit={!!config?.configured}
            onChanged={load}
            setBanner={setBanner}
          />
        ))
      )}
    </div>
  );
};

// ---- one draft card --------------------------------------------------------

const STATUS_META: Record<ReportDraftStatus, { label: string; key: 'success' | 'warning' | 'error' | 'primary' | 'textSecondary' }> = {
  generating: { label: 'Generating…', key: 'warning' },
  pending_review: { label: 'Ready to review', key: 'primary' },
  approved: { label: 'Submitting…', key: 'warning' },
  submitted: { label: 'Submitted', key: 'success' },
  failed: { label: 'Failed', key: 'error' },
  dismissed: { label: 'Dismissed', key: 'textSecondary' },
};

interface DraftCardProps {
  draft: ReportDraft;
  theme: any;
  cardStyle: React.CSSProperties;
  btn: (bg: string) => React.CSSProperties;
  ghostBtn: React.CSSProperties;
  inputStyle: React.CSSProperties;
  canSubmit: boolean;
  onChanged: () => void;
  setBanner: (s: string | null) => void;
}

const DraftCard: React.FC<DraftCardProps> = ({
  draft,
  theme,
  cardStyle,
  btn,
  ghostBtn,
  inputStyle,
  canSubmit,
  onChanged,
  setBanner,
}) => {
  const [accomplished, setAccomplished] = useState(draft.accomplished);
  const [planned, setPlanned] = useState(draft.planned);
  const [challenges, setChallenges] = useState(draft.challenges);
  const [requests, setRequests] = useState<ReportRequestItem[]>(draft.requests || []);
  const [deliverables, setDeliverables] = useState<ReportDeliverableItem[]>(draft.deliverables || []);
  const [carryOver, setCarryOver] = useState<string[]>(draft.carry_over_deliverables || []);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const initialCarryOverRef = useRef<string[]>(draft.carry_over_deliverables || []);

  // Re-sync when the draft content arrives (e.g. generation finished) and we have no local edits.
  useEffect(() => {
    if (!dirty) {
      setAccomplished(draft.accomplished);
      setPlanned(draft.planned);
      setChallenges(draft.challenges);
      setRequests(draft.requests || []);
      setDeliverables(draft.deliverables || []);
      setCarryOver(draft.carry_over_deliverables || []);
    }
  }, [
    draft.accomplished,
    draft.planned,
    draft.challenges,
    draft.requests,
    draft.deliverables,
    draft.carry_over_deliverables,
    dirty,
  ]);

  const meta = STATUS_META[draft.status];
  const editable = draft.status === 'pending_review' || draft.status === 'failed';
  const labelColor = (theme as any)[meta.key] || theme.textSecondary;

  const cleanRequests = (): ReportRequestItem[] =>
    requests
      .filter((r) => r.team && r.text.trim())
      .map((r) => ({ team: r.team, text: r.text.trim() }));

  const cleanDeliverables = (): ReportDeliverableItem[] =>
    deliverables
      .filter((d) => d.text.trim())
      .map((d) => ({ text: d.text.trim(), ...(d.team ? { team: d.team } : {}) }));

  const buildPatch = () => ({
    accomplished,
    planned,
    challenges,
    requests: cleanRequests(),
    deliverables: cleanDeliverables(),
    carry_over_deliverables: carryOver,
  });

  const save = async () => {
    setBusy(true);
    try {
      await patchReportDraft(draft.id, buildPatch());
      setDirty(false);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    setBusy(true);
    setBanner(null);
    try {
      if (dirty) await patchReportDraft(draft.id, buildPatch());
      const res = await approveReportDraft(draft.id);
      if (!res.ok) setBanner(res.reason || 'Submission is not configured yet.');
      setDirty(false);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async () => {
    setBusy(true);
    try {
      await dismissReportDraft(draft.id);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const field = (
    label: string,
    value: string,
    setter: (v: string) => void
  ) => (
    <div style={{ marginBottom: '10px' }}>
      <div style={{ color: theme.textSecondary, fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
        {label}
      </div>
      {editable ? (
        <textarea
          value={value}
          onChange={(e) => {
            setter(e.target.value);
            setDirty(true);
          }}
          rows={Math.max(2, Math.ceil((value?.length || 0) / 90))}
          style={{ ...inputStyle, width: '100%', resize: 'vertical', boxSizing: 'border-box' }}
        />
      ) : (
        <div style={{ color: theme.text, fontSize: '14px', whiteSpace: 'pre-wrap' }}>
          {value || <span style={{ color: theme.textSecondary }}>—</span>}
        </div>
      )}
    </div>
  );

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
        <h3 style={{ color: theme.text, margin: 0, fontSize: '16px' }}>{personLabel(draft.person)}</h3>
        <span
          style={{
            marginLeft: '12px',
            fontSize: '12px',
            fontWeight: 600,
            color: labelColor,
            border: `1px solid ${labelColor}`,
            borderRadius: '999px',
            padding: '2px 10px',
          }}
        >
          {meta.label}
        </span>
        <span style={{ marginLeft: 'auto', color: theme.textSecondary, fontSize: '12px' }}>
          {draft.label}
        </span>
      </div>

      {draft.status === 'generating' ? (
        <div style={{ color: theme.textSecondary, fontSize: '14px' }}>
          Pulling task data and drafting…
        </div>
      ) : (
        <>
          {field('Accomplished', accomplished, setAccomplished)}
          {field('Planned', planned, setPlanned)}
          {field('Challenges', challenges, setChallenges)}

          <RequestsEditor
            rows={requests}
            setRows={(rows) => {
              setRequests(rows);
              setDirty(true);
            }}
            editable={editable}
            theme={theme}
            inputStyle={inputStyle}
            ghostBtn={ghostBtn}
          />

          {draft.period_type === 'monthly' && (
            <DeliverablesEditor
              rows={deliverables}
              setRows={(rows) => {
                setDeliverables(rows);
                setDirty(true);
              }}
              editable={editable}
              theme={theme}
              inputStyle={inputStyle}
              ghostBtn={ghostBtn}
            />
          )}

          {draft.period_type === 'monthly' && initialCarryOverRef.current.length > 0 && (
            <div style={{ color: theme.textSecondary, fontSize: '13px', marginBottom: '10px' }}>
              {carryOver.length > 0 ? (
                <>
                  Carrying {carryOver.length} unfinished deliverable{carryOver.length === 1 ? '' : 's'} into
                  next month.
                  {editable && (
                    <button
                      onClick={() => {
                        setCarryOver([]);
                        setDirty(true);
                      }}
                      style={{ ...ghostBtn, marginLeft: '10px', padding: '4px 10px', fontSize: '12px' }}
                    >
                      Carry none instead
                    </button>
                  )}
                </>
              ) : (
                <>
                  Carrying none of this month's unfinished deliverables into next month.
                  {editable && (
                    <button
                      onClick={() => {
                        setCarryOver(initialCarryOverRef.current);
                        setDirty(true);
                      }}
                      style={{ ...ghostBtn, marginLeft: '10px', padding: '4px 10px', fontSize: '12px' }}
                    >
                      Carry {initialCarryOverRef.current.length} again
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {draft.period_type === 'monthly' && (
            <div style={{ color: theme.textSecondary, fontSize: '12px', marginBottom: '10px' }}>
              Deliverables: {draft.deliverables_completed ?? 0} / {draft.deliverables_total ?? 0}
            </div>
          )}
          {draft.error && (
            <div style={{ color: theme.error, fontSize: '12px', marginBottom: '10px' }}>
              {draft.error}
            </div>
          )}
          {(draft.detail_email_sent_at || draft.detail_email_error) && (
            <div
              style={{
                fontSize: '12px',
                marginBottom: '10px',
                color: draft.detail_email_error ? theme.error : theme.textSecondary,
              }}
            >
              {draft.detail_email_error
                ? `The detailed report email to Joshua did not go out: ${draft.detail_email_error}`
                : `Detailed report emailed to Joshua on ${new Date(
                    draft.detail_email_sent_at as string
                  ).toLocaleString()}.`}
            </div>
          )}
          {draft.status === 'submitted' ? (
            <div style={{ color: theme.success, fontSize: '13px', fontWeight: 600 }}>
              Submitted to the check-ins{draft.checkins_report_id ? ` (#${draft.checkins_report_id})` : ''}.
            </div>
          ) : (
            editable && (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  onClick={approve}
                  disabled={busy || !canSubmit}
                  title={canSubmit ? '' : 'Submission not configured yet'}
                  style={{
                    ...btn(theme.success),
                    opacity: canSubmit ? 1 : 0.5,
                    cursor: canSubmit ? 'pointer' : 'not-allowed',
                  }}
                >
                  Approve &amp; submit
                </button>
                <button onClick={save} disabled={busy || !dirty} style={btn(theme.primary)}>
                  Save edits
                </button>
                <button onClick={dismiss} disabled={busy} style={ghostBtn}>
                  Dismiss
                </button>
              </div>
            )
          )}
        </>
      )}
    </div>
  );
};

// ---- requests / deliverables row editors -----------------------------------

interface RequestsEditorProps {
  rows: ReportRequestItem[];
  setRows: (rows: ReportRequestItem[]) => void;
  editable: boolean;
  theme: any;
  inputStyle: React.CSSProperties;
  ghostBtn: React.CSSProperties;
}

const RequestsEditor: React.FC<RequestsEditorProps> = ({
  rows,
  setRows,
  editable,
  theme,
  inputStyle,
  ghostBtn,
}) => {
  const removeBtn: React.CSSProperties = { ...ghostBtn, padding: '4px 10px', fontSize: '12px' };

  const updateRow = (i: number, patch: Partial<ReportRequestItem>) => {
    setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };
  const removeRow = (i: number) => {
    setRows(rows.filter((_, idx) => idx !== i));
  };
  const addRow = () => {
    setRows([...rows, { team: '', text: '' }]);
  };

  return (
    <div style={{ marginBottom: '10px' }}>
      <div style={{ color: theme.textSecondary, fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
        Requests to other teams
      </div>
      {editable ? (
        <>
          {rows.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '6px', alignItems: 'center' }}>
              <select
                value={r.team}
                onChange={(e) => updateRow(i, { team: e.target.value })}
                style={{ ...inputStyle, width: '190px', flexShrink: 0 }}
              >
                <option value="">Select a team</option>
                {REPORT_TEAMS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={r.text}
                onChange={(e) => updateRow(i, { text: e.target.value })}
                placeholder="What do you need from them?"
                style={{ ...inputStyle, flex: 1, boxSizing: 'border-box' }}
              />
              <button onClick={() => removeRow(i)} style={removeBtn}>
                Remove
              </button>
            </div>
          ))}
          {rows.length === 0 && (
            <div style={{ color: theme.textSecondary, fontSize: '13px', marginBottom: '6px' }}>
              Nothing to request from another team this period.
            </div>
          )}
          <button onClick={addRow} style={{ ...ghostBtn, padding: '5px 12px', fontSize: '12px' }}>
            Add request
          </button>
        </>
      ) : rows.length === 0 ? (
        <div style={{ color: theme.textSecondary, fontSize: '13px' }}>Nothing requested this period.</div>
      ) : (
        rows.map((r, i) => (
          <div key={i} style={{ color: theme.text, fontSize: '13px', marginBottom: '4px' }}>
            <strong>{r.team}:</strong> {r.text}
          </div>
        ))
      )}
    </div>
  );
};

interface DeliverablesEditorProps {
  rows: ReportDeliverableItem[];
  setRows: (rows: ReportDeliverableItem[]) => void;
  editable: boolean;
  theme: any;
  inputStyle: React.CSSProperties;
  ghostBtn: React.CSSProperties;
}

const DeliverablesEditor: React.FC<DeliverablesEditorProps> = ({
  rows,
  setRows,
  editable,
  theme,
  inputStyle,
  ghostBtn,
}) => {
  const removeBtn: React.CSSProperties = { ...ghostBtn, padding: '4px 10px', fontSize: '12px' };

  const updateRow = (i: number, patch: Partial<ReportDeliverableItem>) => {
    setRows(rows.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  };
  const removeRow = (i: number) => {
    setRows(rows.filter((_, idx) => idx !== i));
  };
  const addRow = () => {
    setRows([...rows, { text: '', team: '' }]);
  };

  return (
    <div style={{ marginBottom: '10px' }}>
      <div style={{ color: theme.textSecondary, fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
        Deliverables proposed for next month
      </div>
      {editable ? (
        <>
          {rows.map((d, i) => (
            <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '6px', alignItems: 'center' }}>
              <select
                value={d.team || ''}
                onChange={(e) => updateRow(i, { team: e.target.value })}
                style={{ ...inputStyle, width: '190px', flexShrink: 0 }}
              >
                <option value="">No team</option>
                {REPORT_TEAMS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={d.text}
                onChange={(e) => updateRow(i, { text: e.target.value })}
                placeholder="Deliverable for next month"
                style={{ ...inputStyle, flex: 1, boxSizing: 'border-box' }}
              />
              <button onClick={() => removeRow(i)} style={removeBtn}>
                Remove
              </button>
            </div>
          ))}
          {rows.length === 0 && (
            <div style={{ color: theme.textSecondary, fontSize: '13px', marginBottom: '6px' }}>
              No deliverables proposed for next month yet.
            </div>
          )}
          <button onClick={addRow} style={{ ...ghostBtn, padding: '5px 12px', fontSize: '12px' }}>
            Add deliverable
          </button>
        </>
      ) : rows.length === 0 ? (
        <div style={{ color: theme.textSecondary, fontSize: '13px' }}>
          No deliverables proposed for next month.
        </div>
      ) : (
        rows.map((d, i) => (
          <div key={i} style={{ color: theme.text, fontSize: '13px', marginBottom: '4px' }}>
            {d.team ? <strong>{d.team}:</strong> : null} {d.text}
          </div>
        ))
      )}
    </div>
  );
};

export default Reports;
